import { useState } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { usePublicData } from "@/lib/publicData";
import { resolveMedia } from "@/lib/api";
import { Phone, Mail, MapPin, Menu, X, Heart, Facebook, Instagram, Linkedin, Twitter, LifeBuoy, Users, Building2, HelpCircle, ArrowRight } from "lucide-react";
import { Container } from "@/components/public/parts";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useEffect } from "react";

const NAV = [
  ["Services", "/services"], ["About", "/about"], ["How It Works", "/how-it-works"],
  ["Gallery", "/gallery"], ["Careers", "/careers"], ["Contact", "/contact"],
];
const FOOThER_MORE = [["Service Areas", "/service-areas"], ["Resources", "/resources"], ["FAQ", "/faq"], ["Request Services", "/request-services"], ["Make a Referral", "/referral"]];

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

export default function PublicLayout() {
  const data = usePublicData();
  const s = data?.settings || {};
  const brand = s.brand || {};
  const logo = brand.logo_url ? resolveMedia(brand.logo_url) : null;
  const social = brand.social || {};
  const [nav, setNav] = useState(false);
  const [gs, setGs] = useState(false);
  const navigate = useNavigate();
  const go = (to) => { setGs(false); setNav(false); navigate(to); };
  const OPTIONS = [
    [LifeBuoy, "I need support", "For myself or a loved one", "/request-services"],
    [Users, "I'm referring someone", "Case managers & families", "/referral"],
    [Building2, "I'm a professional / organization", "Partner with us", "/referral"],
    [HelpCircle, "I have a question", "General inquiries", "/contact"],
  ];

  return (
    <div className="bg-cream text-foreground min-h-screen flex flex-col">
      <ScrollTop />
      <header className="sticky top-0 z-50 bg-cream/95 backdrop-blur border-b border-border">
        <Container className="h-[92px] lg:h-[112px] flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center shrink-0" aria-label="Love For Mankind home" data-testid="public-logo">
            {logo ? <img src={logo} alt="Love For Mankind" className="h-[52px] sm:h-[64px] lg:h-[80px] w-auto object-contain" />
              : <span className="font-heading font-bold text-lg">Love For Mankind</span>}
          </Link>
          <nav className="hidden lg:flex items-center gap-7" aria-label="Primary">
            {NAV.map(([label, to]) => (
              <NavLink key={to} to={to} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) => `text-[15px] font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-primary"}`}>{label}</NavLink>
            ))}
          </nav>
          <div className="hidden lg:flex items-center gap-3 shrink-0">
            <button onClick={() => setGs(true)} className="rounded-full bg-primary text-primary-foreground font-semibold h-11 px-7 hover:bg-primary/90 transition-colors" data-testid="get-started-btn">Get Started</button>
          </div>
          <button className="lg:hidden p-2" onClick={() => setNav(!nav)} aria-label="Toggle menu" data-testid="mobile-nav-toggle">{nav ? <X /> : <Menu />}</button>
        </Container>
        {nav && (
          <div className="lg:hidden border-t border-border px-5 py-4 flex flex-col gap-2 bg-cream">
            {NAV.map(([label, to]) => <Link key={to} to={to} onClick={() => setNav(false)} className="py-1.5 text-[15px] font-medium">{label}</Link>)}
            <button onClick={() => { setNav(false); setGs(true); }} className="rounded-full bg-primary text-primary-foreground font-semibold w-full h-11 mt-2" data-testid="get-started-btn-mobile">Get Started</button>
            <Link to="/login" onClick={() => setNav(false)} className="text-center text-sm text-muted-foreground mt-1">Staff Login</Link>
          </div>
        )}
      </header>

      <main className="flex-1"><Outlet /></main>

      <footer className="bg-foreground text-cream">
        <Container className="py-14 grid md:grid-cols-4 gap-10">
          <div className="md:col-span-1">
            {logo ? <div className="inline-flex bg-cream rounded-xl p-3"><img src={logo} alt="Love For Mankind" className="h-12 w-auto object-contain" /></div>
              : <span className="font-heading font-bold text-xl">Love For Mankind</span>}
            <p className="text-cream/70 text-[15px] mt-5 leading-[1.7]">{s.tagline || "Compassionate Support. Greater Independence."}</p>
            <div className="flex gap-3 mt-5">
              {social.facebook && <a href={social.facebook} className="text-cream/70 hover:text-gold"><Facebook className="w-5 h-5" /></a>}
              {social.instagram && <a href={social.instagram} className="text-cream/70 hover:text-gold"><Instagram className="w-5 h-5" /></a>}
              {social.linkedin && <a href={social.linkedin} className="text-cream/70 hover:text-gold"><Linkedin className="w-5 h-5" /></a>}
              {social.twitter && <a href={social.twitter} className="text-cream/70 hover:text-gold"><Twitter className="w-5 h-5" /></a>}
            </div>
          </div>
          <div>
            <p className="font-heading font-semibold mb-4 text-gold">Explore</p>
            <ul className="space-y-2.5 text-[15px] text-cream/70">
              {NAV.map(([label, to]) => <li key={to}><Link to={to} className="hover:text-cream">{label}</Link></li>)}
            </ul>
          </div>
          <div>
            <p className="font-heading font-semibold mb-4 text-gold">More</p>
            <ul className="space-y-2.5 text-[15px] text-cream/70">
              {FOOThER_MORE.map(([label, to]) => <li key={to}><Link to={to} className="hover:text-cream">{label}</Link></li>)}
              <li><Link to="/login" className="hover:text-cream">Staff Login</Link></li>
            </ul>
          </div>
          <div>
            <p className="font-heading font-semibold mb-4 text-gold">Get in touch</p>
            <ul className="space-y-2.5 text-[15px] text-cream/70">
              <li className="flex items-center gap-2"><Phone className="w-4 h-4" /> {s.phone}</li>
              <li className="flex items-center gap-2"><Mail className="w-4 h-4" /> {s.email}</li>
              <li className="flex items-start gap-2"><MapPin className="w-4 h-4 mt-0.5" /> {s.address}</li>
            </ul>
          </div>
        </Container>
        <div className="border-t border-cream/15">
          <Container className="py-5 text-center text-[13px] text-cream/60">
            © 2026 Love For Mankind. All rights reserved. Designed by LC Creative Studio.
          </Container>
        </div>
      </footer>

      <Dialog open={gs} onOpenChange={setGs}>
        <DialogContent className="max-w-lg" data-testid="get-started-modal">
          <DialogHeader><DialogTitle className="text-2xl">How can we help?</DialogTitle></DialogHeader>
          <div className="grid gap-3 mt-2">
            {OPTIONS.map(([Icon, title, sub, to]) => (
              <button key={title} onClick={() => go(to)} data-testid={`gs-option-${to.replace("/", "")}`}
                className="group flex items-center gap-4 rounded-xl border border-border p-4 text-left hover:border-primary hover:bg-accent transition-colors">
                <span className="w-11 h-11 rounded-xl bg-secondary flex items-center justify-center shrink-0 group-hover:bg-primary transition-colors"><Icon className="w-5 h-5 text-primary group-hover:text-primary-foreground transition-colors" /></span>
                <span className="flex-1"><span className="block font-heading font-semibold">{title}</span><span className="block text-sm text-muted-foreground">{sub}</span></span>
                <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
